"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { FilterTypeConfig } from "@/components/ui/table/table-filters";
import { toTableFilterTypes } from "./definitions";
import { applyLocationDefaults, enhanceLocationFilterDefinitions, locationDefaultIdentity, locationFilterValues, useTableFilterDefaultContext } from "./location-defaults";
import { applyControlValue, applyFilterValue, canonicalFilterKey, hydrateFilterRequest, isFilterActive, normalizeFilterRequest, projectFilterControls, unmanagedFilterValues, writeFilterUrl } from "./state";
import type { FilterDefinition, FilterKey, RequestUpdater, TableFilterControls } from "./types";
interface UseTableFiltersOptions<Request extends object> {
  defaults: Request;
  urlNamespace?: string;
  includeTableState?: boolean;
  resetPageOnChange?: boolean;
  includeArchivedLocations?: boolean;
}
export interface TableFiltersState<Request extends object> {
  request: Request;
  queryKey: Request;
  controls: TableFilterControls<Request>;
  set: <Key extends FilterKey<Request>>(key: Key, value: Request[Key] | undefined) => void;
  setRequest: (update: RequestUpdater<Request>) => void;
  setMany: (values: Partial<Request>) => void;
  replace: (values: Partial<Request>) => void;
  clear: (key: FilterKey<Request>) => void;
  clearAll: () => void;
  isActive: (key: FilterKey<Request>) => boolean;
}
interface CoordinatedUrlWrites {
  latestQuery: string;
  pendingQueries: Set<string>;
}
const coordinatedUrlWritesByPathname = new Map<string, CoordinatedUrlWrites>();
function coordinatedSearchParams(pathname: string, searchParams: URLSearchParams): URLSearchParams {
  return new URLSearchParams(coordinatedUrlWritesByPathname.get(pathname)?.latestQuery ?? searchParams.toString());
}
function trackCoordinatedUrlWrite(pathname: string, query: string): void {
  const state = coordinatedUrlWritesByPathname.get(pathname) ?? {
    latestQuery: query,
    pendingQueries: new Set<string>()
  } satisfies CoordinatedUrlWrites;
  state.latestQuery = query;
  state.pendingQueries.add(query);
  coordinatedUrlWritesByPathname.set(pathname, state);
}
function reconcileCoordinatedUrl(pathname: string, query: string): void {
  const state = coordinatedUrlWritesByPathname.get(pathname);
  if (!state) return;
  if (!state.pendingQueries.delete(query)) {
    coordinatedUrlWritesByPathname.delete(pathname);
    return;
  }
  if (state.latestQuery === query) {
    coordinatedUrlWritesByPathname.delete(pathname);
  }
}
export function useTableFilters<Request extends object>(definitions: readonly FilterDefinition<Request>[], {
  defaults,
  urlNamespace,
  includeTableState = true,
  resetPageOnChange = true,
  includeArchivedLocations = false
}: UseTableFiltersOptions<Request>): TableFiltersState<Request> {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const defaultContext = useTableFilterDefaultContext(includeArchivedLocations);
  const enhancedDefinitions = useMemo(() => enhanceLocationFilterDefinitions(definitions, defaultContext), [definitions, defaultContext]);
  const appliedDefaultIdentitiesRef = useRef(new Set<string>());
  const explicitEmptyClearKeysRef = useRef(new Set<string>());
  const normalizeBase = useCallback((request: Partial<Request>) => normalizeFilterRequest(enhancedDefinitions, request), [enhancedDefinitions]);
  const normalizeWithDefaults = useCallback((request_0: Partial<Request>, {
    force = false,
    track = true
  }: {
    force?: boolean;
    track?: boolean;
  } = {}) => {
    const normalized = normalizeBase(request_0);
    const identity = locationDefaultIdentity(enhancedDefinitions, defaultContext);
    if (!force && appliedDefaultIdentitiesRef.current.has(identity)) {
      return normalized;
    }
    if (track) appliedDefaultIdentitiesRef.current.add(identity);
    return applyLocationDefaults(enhancedDefinitions, normalized, defaultContext, explicitEmptyClearKeysRef.current);
  }, [defaultContext, enhancedDefinitions, normalizeBase]);
  const defaultsRef = useRef(normalizeFilterRequest(enhancedDefinitions, defaults));
  defaultsRef.current = normalizeFilterRequest(enhancedDefinitions, defaults);
  const [request_1, setRequestState] = useState<Request>(() => normalizeWithDefaults(hydrateFilterRequest(enhancedDefinitions, new URLSearchParams(searchParams.toString()), defaultsRef.current, {
    namespace: urlNamespace,
    includeTableState
  }), {
    force: true,
    track: false
  }));
  const requestRef = useRef(request_1);
  requestRef.current = request_1;
  const pendingUrlWritesRef = useRef(new Set<string>());
  const latestUrlWriteRef = useRef<string | null>(null);
  const hydratedUrlRef = useRef(searchParams.toString());
  const hydratedDefaultIdentityRef = useRef(locationDefaultIdentity(enhancedDefinitions, defaultContext));
  const releaseExplicitEmptyClear = useCallback((values: Partial<Request>) => {
    const mutable = values as Record<string, unknown>;
    enhancedDefinitions.forEach(definition => {
      if (definition.control !== "location") return;
      if (definition.requestKeys.some(key => {
        const value = mutable[key as string];
        return !(value === undefined || value === null || value === "" || value === false || Array.isArray(value) && value.length === 0);
      })) {
        explicitEmptyClearKeysRef.current.delete(definition.key);
      }
    });
  }, [enhancedDefinitions]);
  const releaseExplicitEmptyClearFromUrl = useCallback((params: URLSearchParams) => {
    enhancedDefinitions.forEach(definition_0 => {
      if (definition_0.control !== "location") return;
      const hasUrlValue = definition_0.urlKeys.some(urlKey => {
        const namespacedKey = urlNamespace ? `${urlNamespace}.${urlKey}` : urlKey;
        return params.has(namespacedKey);
      });
      if (hasUrlValue) {
        explicitEmptyClearKeysRef.current.delete(definition_0.key);
      }
    });
  }, [enhancedDefinitions, urlNamespace]);
  useEffect(() => {
    const currentUrl = searchParams.toString();
    const urlChanged = hydratedUrlRef.current !== currentUrl;
    hydratedUrlRef.current = currentUrl;
    const defaultIdentity = locationDefaultIdentity(enhancedDefinitions, defaultContext);
    const defaultsChanged = hydratedDefaultIdentityRef.current !== defaultIdentity;
    hydratedDefaultIdentityRef.current = defaultIdentity;
    reconcileCoordinatedUrl(pathname, currentUrl);
    if (pendingUrlWritesRef.current.delete(currentUrl)) {
      if (latestUrlWriteRef.current === currentUrl) {
        pendingUrlWritesRef.current.clear();
        latestUrlWriteRef.current = null;
      }
      return;
    }

    // Our own write may not have landed yet; rebuilding from the URL it replaces drops it.
    if (urlChanged) latestUrlWriteRef.current = null;
    const params_0 = new URLSearchParams(latestUrlWriteRef.current ?? currentUrl);
    releaseExplicitEmptyClearFromUrl(params_0);
    // Re-hydrating rebuilds the request from defaults + URL, so values no
    // definition owns (a cohort's saved appointment criteria, say) have no
    // representation to be restored from. Carry them across, or a re-hydration
    // silently widens the request — a cohort preview turns into the full
    // patient list. This effect also runs when only the definitions' identity
    // changed (locations resolving), where the URL is not the newer state.
    // The location default lives only in the request, never the URL, so when
    // neither the URL nor the default context changed keep the current
    // location values too. A context change (switching current location,
    // A → B → A included) re-evaluates the default instead.
    const hydrated = normalizeWithDefaults({
      ...hydrateFilterRequest(enhancedDefinitions, params_0, defaultsRef.current, {
        namespace: urlNamespace,
        includeTableState
      }),
      ...unmanagedFilterValues(enhancedDefinitions, requestRef.current),
      ...(urlChanged || defaultsChanged ? {} : locationFilterValues(enhancedDefinitions, requestRef.current))
    }, {
      force: defaultsChanged
    });
    if (JSON.stringify(canonicalFilterKey(hydrated, enhancedDefinitions)) === JSON.stringify(canonicalFilterKey(requestRef.current, enhancedDefinitions))) {
      return;
    }
    requestRef.current = hydrated;
    setRequestState(hydrated);
  }, [defaultContext, enhancedDefinitions, includeTableState, normalizeWithDefaults, releaseExplicitEmptyClearFromUrl, pathname, searchParams, urlNamespace]);
  const syncUrl = useCallback((nextRequest: Request) => {
    const current = coordinatedSearchParams(pathname, searchParams);
    const next = writeFilterUrl(enhancedDefinitions, current, nextRequest, {
      namespace: urlNamespace,
      includeTableState
    });
    if (next.toString() === current.toString()) return;
    const query = next.toString();
    trackCoordinatedUrlWrite(pathname, query);
    pendingUrlWritesRef.current.add(query);
    latestUrlWriteRef.current = query;
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false
    });
  }, [enhancedDefinitions, includeTableState, pathname, router, searchParams, urlNamespace]);
  const commit = useCallback((next_0: Request) => {
    requestRef.current = next_0;
    setRequestState(next_0);
    syncUrl(next_0);
  }, [syncUrl]);
  const setRequest = useCallback((update: RequestUpdater<Request>) => {
    const updated = typeof update === "function" ? update(requestRef.current) : update;
    releaseExplicitEmptyClear(updated);
    commit(normalizeWithDefaults({
      ...defaultsRef.current,
      ...updated
    }));
  }, [commit, normalizeWithDefaults, releaseExplicitEmptyClear]);
  const setMany = useCallback((values_0: Partial<Request>) => {
    releaseExplicitEmptyClear(values_0);
    const resetPage = resetPageOnChange && ("page" in defaultsRef.current || "page" in requestRef.current);
    commit(normalizeFilterRequest(enhancedDefinitions, {
      ...defaultsRef.current,
      ...requestRef.current,
      ...values_0,
      ...(resetPage ? {
        page: 1
      } : {})
    }));
  }, [commit, enhancedDefinitions, releaseExplicitEmptyClear, resetPageOnChange]);
  const replace = useCallback((values_1: Partial<Request>) => {
    releaseExplicitEmptyClear(values_1);
    const resetPage_0 = resetPageOnChange && ("page" in defaultsRef.current || "page" in requestRef.current);
    commit(normalizeWithDefaults({
      ...defaultsRef.current,
      ...values_1,
      ...(resetPage_0 ? {
        page: 1
      } : {})
    }));
  }, [commit, normalizeWithDefaults, releaseExplicitEmptyClear, resetPageOnChange]);
  const set = useCallback(<Key extends FilterKey<Request>,>(key_0: Key, value_0: Request[Key] | undefined) => {
    const definition_1 = enhancedDefinitions.find(candidate => candidate.requestKeys.includes(key_0));
    const shouldReapplyDefault = value_0 === undefined && definition_1?.clearPolicy === "default";
    if (definition_1?.control === "location") {
      if (value_0 === undefined) {
        if (definition_1.clearPolicy === "empty") {
          explicitEmptyClearKeysRef.current.add(definition_1.key);
        } else {
          explicitEmptyClearKeysRef.current.delete(definition_1.key);
        }
      } else {
        releaseExplicitEmptyClear({
          [key_0]: value_0
        } as Partial<Request>);
      }
    }
    const next_1 = applyFilterValue(enhancedDefinitions, defaultsRef.current, requestRef.current, key_0, value_0, resetPageOnChange);
    commit(shouldReapplyDefault ? normalizeWithDefaults(next_1, {
      force: true
    }) : next_1);
  }, [commit, enhancedDefinitions, normalizeWithDefaults, releaseExplicitEmptyClear, resetPageOnChange]);
  const setControl = useCallback((key_1: string, value_1: unknown) => {
    const definition_2 = enhancedDefinitions.find(candidate_0 => candidate_0.key === key_1);
    const shouldReapplyDefault_0 = value_1 === undefined && definition_2?.clearPolicy === "default";
    if (definition_2?.control === "location") {
      if (value_1 === undefined) {
        if (definition_2.clearPolicy === "empty") {
          explicitEmptyClearKeysRef.current.add(definition_2.key);
        } else {
          explicitEmptyClearKeysRef.current.delete(definition_2.key);
        }
      } else {
        releaseExplicitEmptyClear(definition_2.writeControl(requestRef.current, value_1));
      }
    }
    const next_2 = applyControlValue(enhancedDefinitions, defaultsRef.current, requestRef.current, key_1, value_1, resetPageOnChange);
    commit(shouldReapplyDefault_0 ? normalizeWithDefaults(next_2, {
      force: true
    }) : next_2);
  }, [commit, enhancedDefinitions, normalizeWithDefaults, releaseExplicitEmptyClear, resetPageOnChange]);
  const clear = useCallback((key_2: FilterKey<Request>) => set(key_2, undefined), [set]);
  const clearAll = useCallback(() => {
    explicitEmptyClearKeysRef.current.clear();
    const current_0 = requestRef.current as Record<string, unknown>;
    const hasPage = "page" in current_0 || "page" in defaultsRef.current;
    commit(normalizeWithDefaults({
      ...defaultsRef.current,
      ...(resetPageOnChange && hasPage ? {
        page: 1
      } : {}),
      ...(hasPage && current_0.perPage !== undefined ? {
        perPage: current_0.perPage
      } : {})
    } as Partial<Request>, {
      force: true
    }));
  }, [commit, normalizeWithDefaults, resetPageOnChange]);
  const resolvedFilterTypes = useMemo(() => toTableFilterTypes(enhancedDefinitions), [enhancedDefinitions]);
  const resolveFilterTypes = useCallback((baseFilterTypes: readonly FilterTypeConfig[] = resolvedFilterTypes): FilterTypeConfig[] => {
    const generatedByKey = new Map(resolvedFilterTypes.map(filterType => [filterType.filterKey, filterType]));
    return baseFilterTypes.map(baseFilterType => {
      const generatedFilterType = generatedByKey.get(baseFilterType.filterKey);
      if (!generatedFilterType) return {
        ...baseFilterType
      };
      const hasCallerOptions = Boolean(baseFilterType.options?.length);
      const options = hasCallerOptions ? baseFilterType.options : generatedFilterType.options;
      return {
        ...generatedFilterType,
        ...baseFilterType,
        ...(options ? {
          options
        } : {})
      };
    });
  }, [resolvedFilterTypes]);
  const queryKey = useMemo(() => canonicalFilterKey(request_1, enhancedDefinitions), [enhancedDefinitions, request_1]);
  const controls = useMemo<TableFilterControls<Request>>(() => ({
    values: projectFilterControls(enhancedDefinitions, request_1),
    filterTypes: resolvedFilterTypes,
    resolveFilterTypes,
    set: setControl,
    setMany,
    replace
  }), [enhancedDefinitions, replace, request_1, resolveFilterTypes, resolvedFilterTypes, setControl, setMany]);
  return {
    request: request_1,
    queryKey,
    controls,
    set,
    setRequest,
    setMany,
    replace,
    clear,
    clearAll,
    isActive: key_3 => isFilterActive(enhancedDefinitions, request_1, key_3)
  };
}